import { css, html, nothing, svg } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { repeat } from 'lit/directives/repeat.js'
import { when } from 'lit/directives/when.js'
import { SchmancyElement } from '@schmancy/mixins/index'
import '@schmancy/primitives/surface'
import '@schmancy/primitives/typography'
import {
	CHART_W,
	ChartMixin,
	FILL,
	POINT_RADIUS,
	STROKE_OUTLINE,
	nearestChartPoint,
	renderChartTooltipCard,
	type ChartHoveredState,
	type ChartLayout,
	type ChartLayoutPoint,
} from './chart.mixin'
import type { ChartPoint, ChartSelectEvent } from './types'

/** Radius of a peak's own dot; its halo is this plus 4. Exported so the
 *  roundness assertions name one radius rather than a second copy of it. */
export const PEAK_RADIUS = 6

/** `'auto'` thins responsively via `@container` queries as the host grows. */
export type AreaChartXLabels = 'none' | 'all' | 'auto'

/**
 * Smooth trend chart: SVG-rendered (monotone cubic curve), optional dashed
 * gridlines, top-N peaks highlighted with a larger marker + halo, and an
 * internal hover tooltip.
 */
@customElement('schmancy-area-chart')
export class SchmancyAreaChart extends ChartMixin(SchmancyElement) {
	static styles = [
		css`
			[part='point'][role='button'] {
				cursor: pointer;
				outline: none;
			}

			[part='point'][role='button']:hover [part='point-mark'] {
				filter: brightness(1.12);
			}

			[part='point'][role='button']:focus-visible [part='point-mark'] {
				stroke: currentColor;
				stroke-width: 3;
			}
		`,
	]

	@property({ type: Number }) gridlines = 0
	@property({ type: Number }) peaks = 0
	@property({ type: Boolean }) tooltip = false
	/** Makes the plotted points themselves activation targets. The area and
	 * line remain the same visual geometry; each point mark gains a focusable
	 * SVG target and emits the existing `select` event. */
	@property({ type: Boolean }) interactive = false
	@property({ attribute: false }) ariaLabelFor?: (p: ChartPoint) => string
	@property({ type: Boolean }) override animated = false
	@property({ type: String }) xLabels: AreaChartXLabels = 'none'

	override curve = 'smooth' as const

	/** Every point carries this chart's own dot, a peak a larger one. */
	override marksPoint(): boolean {
		return true
	}

	override defaultPadding() {
		return { top: 24, right: 20, bottom: this.xLabels !== 'none' ? 40 : 20, left: 20 }
	}

	@state() private hovered: ChartHoveredState = { visible: false, index: -1 }

	override handlePointerMove = (e: PointerEvent): void => {
		if (!this.tooltip || !this.layout) return
		const el = this.plotRef.value
		if (!el) return
		const rect = el.getBoundingClientRect()
		if (rect.width === 0) return
		this.hovered = nearestChartPoint(this.layout, rect, e, this.hovered)
	}

	override handlePointerLeave = (): void => {
		this.hovered = { ...this.hovered, visible: false }
	}

	override handlePlotClick = (event: MouseEvent): void => {
		if (!this.interactive || !this.layout) return
		const target = event.target
		// Point groups own their click. The guard keeps the continuous plot
		// surface from dispatching a second selection as the event bubbles out.
		if (target instanceof Element && target.closest('[part="point"]')) return
		const rect = this.plotRef.value?.getBoundingClientRect()
		if (!rect || rect.width === 0) return
		const x = Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 1)
		const point = this.layout.points.reduce((closest, candidate) =>
			Math.abs(candidate.x / CHART_W - x) < Math.abs(closest.x / CHART_W - x) ? candidate : closest,
		)
		this.activatePoint(point, event)
	}

	private peakIndices(layout: ChartLayout): Set<number> {
		if (this.peaks <= 0) return new Set()
		return new Set(
			[...layout.points]
				.toSorted((a, b) => b.point.value - a.point.value)
				.slice(0, this.peaks)
				.map(p => p.index),
		)
	}

	override renderBackgroundMarks(layout: ChartLayout) {
		if (!this.gridlines) return nothing
		return svg`${repeat(
			Array.from({ length: this.gridlines }, (_, i) => i),
			i => i,
			i => {
				const fraction = (i + 1) / (this.gridlines + 1)
				const y = layout.padding.top + fraction * layout.chartHeight
				// The dash runs along x, the one axis this viewBox stretches, so
				// without non-scaling-stroke — which strokes in screen space — a 3-unit
				// dash paints 3 * plotWidth / CHART_W px and the rhythm tracks the
				// container instead of holding still.
				return svg`
					<line
						x1="0"
						y1=${y}
						x2=${CHART_W}
						y2=${y}
						class=${STROKE_OUTLINE}
						stroke-width="1"
						stroke-opacity="0.4"
						stroke-dasharray="3 3"
						vector-effect="non-scaling-stroke"
					></line>
				`
			},
		)}`
	}

	override renderUnstretchedMarks(layout: ChartLayout) {
		const peakIndices = this.peakIndices(layout)
		return svg`${repeat(
			layout.points,
			p => p.index,
			p => {
				const isPeak = peakIndices.has(p.index)
				const mark = svg`
					${when(
						isPeak,
						() =>
							svg`<circle cx="${this.xPercent(p)}%" cy=${p.y} r=${PEAK_RADIUS + 4} class=${FILL.primary} fill-opacity="0.2"></circle>`,
					)}
					<circle part="point-mark" cx="${this.xPercent(p)}%" cy=${p.y} r=${isPeak ? PEAK_RADIUS : POINT_RADIUS} class=${FILL.primary}></circle>
				`
				if (!this.interactive) return svg`<g part="point">${mark}</g>`
				return svg`
					<g
						part="point"
						role="button"
						tabindex="0"
						aria-label=${this.ariaLabelFor?.(p.point) ?? `${p.point.label ?? ''}: ${this.formatValue(p.point.value, p.point)}`}
						data-point-index=${p.index}
						class="chart-point-control"
						@click=${(event: MouseEvent) => this.activatePoint(p, event)}
						@keydown=${(event: KeyboardEvent) => this.activatePointFromKeyboard(p, event)}
					>
						<title>${p.point.label ?? ''}: ${this.formatValue(p.point.value, p.point)}</title>
						<circle part="point-hit" cx="${this.xPercent(p)}%" cy=${p.y} r="12" fill="transparent"></circle>
						${mark}
					</g>
				`
			},
		)}`
	}

	private activatePoint(point: ChartLayoutPoint, _event: Event): void {
		const event: ChartSelectEvent = new CustomEvent('select', {
			detail: { index: point.index, point: point.point, data: point.point.data },
			bubbles: true,
			composed: true,
		})
		this.dispatchEvent(event)
	}

	private activatePointFromKeyboard(point: ChartLayoutPoint, event: KeyboardEvent): void {
		if (event.key !== 'Enter' && event.key !== ' ') return
		event.preventDefault()
		this.activatePoint(point, event)
	}

	private renderXLabels(layout: ChartLayout) {
		if (this.xLabels === 'none') return nothing
		// Each rung's `block` only ever ADDS to the rungs below it, so the set a
		// wide container paints is their union — which stays evenly spaced only
		// when the wider strides are exact multiples of the narrower one. Hence
		// one density, and the three rungs as its multiples.
		const lastIndex = layout.points.length - 1
		const density = Math.max(1, Math.ceil(Math.max(1, lastIndex) / 9))
		// density === 1 (N ≤ 10) reuses the 3 / 2 / 1 ladder verbatim.
		const strideBase = density === 1 ? 3 : density * 4
		const strideAt400 = density === 1 ? 2 : density * 2
		const strideAt600 = density
		// The trailing caption is painted at every rung, so a strided one landing
		// less than a full stride short of it is the one pair the stride cannot
		// keep apart on its own — it yields, and the trailing caption stands alone.
		const onStride = (index: number, step: number) =>
			density === 1 ? index % step === 0 : index % step === 0 && lastIndex - index >= step
		return html`
			<div class="absolute inset-x-0 bottom-0.5 h-4 pointer-events-none">
				${repeat(
					layout.points,
					p => p.index,
					p => {
						const thinnable = this.xLabels === 'auto' && !p.isFirst && !p.isLast
						const showAlways = !thinnable || onStride(p.index, strideBase)
						const showAt400 = thinnable && !showAlways && onStride(p.index, strideAt400)
						const showAt600 = thinnable && !showAlways && !showAt400 && onStride(p.index, strideAt600)
						return html`
							<span
								class=${this.classMap({
									'absolute text-[10px] text-surface-on-variant opacity-60 whitespace-nowrap tabular-nums': true,
									'-translate-x-1/2': this.horizontalAlignment(p) === 'center',
									'text-right': this.horizontalAlignment(p) === 'right',
									hidden: thinnable && !showAlways,
									'@min-[400px]:block': showAt400,
									'@min-[600px]:block': showAt600,
								})}
								style=${this.styleMap(this.horizontalAnchorStyle(p))}
								>${p.point.label ?? ''}</span
							>
						`
					},
				)}
			</div>
		`
	}

	override renderOverlays(layout: ChartLayout) {
		if (!this.tooltip) return this.renderXLabels(layout)
		return html`
			${this.renderXLabels(layout)} ${renderChartTooltipCard(layout, this.hovered, (v, p) => this.formatValue(v, p))}
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-area-chart': SchmancyAreaChart
	}
}
